import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import { api, ApiError, isNotAvailable, unwrap } from './client'
import type { components } from './schema'

export type Me = components['schemas']['Me']
export type InboxItem = components['schemas']['InboxItem']
export type OrderSummary = components['schemas']['OrderSummary']
export type OrderDetail = components['schemas']['OrderDetail']
export type OrderStatus = components['schemas']['OrderStatus']
export type TransitionAction = components['schemas']['TransitionAction']
export type InboxState = 'open' | 'acknowledged' | 'all'

export const queryKeys = {
  me: ['me'] as const,
  inbox: ['inbox'] as const,
  inboxState: (state: InboxState) => ['inbox', state] as const,
  orders: ['orders'] as const,
  ordersList: (params: object) => ['orders', 'list', params] as const,
  ordersAll: (params: object) => ['orders', 'all', params] as const,
  areas: (groupBy: string) => ['orders', 'areas', groupBy] as const,
  order: (id: string) => ['orders', 'detail', id] as const,
  nearby: (id: string, radiusM: number) => ['orders', 'nearby', id, radiusM] as const,
  import: (id: string) => ['imports', id] as const,
}

export const meQuery = queryOptions({
  queryKey: queryKeys.me,
  queryFn: async (): Promise<Me> => unwrap(await api.GET('/api/v1/me')),
  staleTime: 60_000,
  retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
})

/** `unavailable`: der Endpunkt existiert im Backend noch nicht (404/501). */
export interface Availability<T> {
  unavailable: boolean
  data: T
}

export function inboxQuery(state: InboxState) {
  return queryOptions({
    queryKey: queryKeys.inboxState(state),
    queryFn: async (): Promise<Availability<InboxItem[]>> => {
      try {
        const page = unwrap(await api.GET('/api/v1/inbox', { params: { query: { state } } }))
        return { unavailable: false, data: page.items }
      } catch (error) {
        if (isNotAvailable(error)) return { unavailable: true, data: [] }
        throw error
      }
    },
    // Ohne Server-Endpunkt nicht pollen; sonst Sicherheitsnetz zusätzlich zu SSE.
    refetchInterval: (query) => (query.state.data?.unavailable ? false : 60_000),
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
  })
}

export type OrderArea = components['schemas']['OrderArea']
export type ImportBatch = components['schemas']['ImportBatch']
export type AreaGroupBy = 'rajon' | 'municipality' | 'postal_code' | 'mdf'

/** Serverseitige Filter der Auftragsliste (höchstens 5 gleichzeitig, sonst 400). */
export interface OrderListParams {
  status?: OrderStatus
  rajon?: string
  municipality?: string
  postalCode?: string
  mdf?: string
  orderType?: string
  assignedOrgId?: string
  q?: string
}

const PAGE_SIZE = 50

async function fetchOrderPage(params: OrderListParams, cursor: string | undefined, limit: number) {
  const { status, ...rest } = params
  return unwrap(
    await api.GET('/api/v1/orders', {
      params: {
        query: {
          ...rest,
          ...(status ? { status: [status] } : {}),
          ...(cursor ? { cursor } : {}),
          limit,
        },
      },
    }),
  )
}

/** Tabelle: seitenweise nachladen (Cursor-Pagination, serverseitig). */
export function ordersInfiniteQuery(params: OrderListParams) {
  return infiniteQueryOptions({
    queryKey: queryKeys.ordersList(params),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => fetchOrderPage(params, pageParam, PAGE_SIZE),
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    retry: false,
  })
}

/** Karte: alle Seiten bis zur Obergrenze laden (Marker brauchen den ganzen Bestand). */
export const MAP_ORDER_CAP = 1000
export function ordersAllQuery(params: OrderListParams) {
  return queryOptions({
    queryKey: queryKeys.ordersAll(params),
    queryFn: async (): Promise<{ items: OrderSummary[]; truncated: boolean }> => {
      const items: OrderSummary[] = []
      let cursor: string | undefined
      do {
        const page = await fetchOrderPage(params, cursor, 200)
        items.push(...page.items)
        cursor = page.nextCursor ?? undefined
      } while (cursor && items.length < MAP_ORDER_CAP)
      return { items, truncated: Boolean(cursor) }
    },
    retry: false,
  })
}

export function areasQuery(groupBy: AreaGroupBy) {
  return queryOptions({
    queryKey: queryKeys.areas(groupBy),
    queryFn: async (): Promise<OrderArea[]> =>
      unwrap(await api.GET('/api/v1/orders/areas', { params: { query: { groupBy } } })),
    retry: false,
  })
}

export interface OrderWithEtag {
  order: OrderDetail
  etag: string | null
}

export function orderQuery(id: string) {
  return queryOptions({
    queryKey: queryKeys.order(id),
    queryFn: async (): Promise<OrderWithEtag> => {
      const result = await api.GET('/api/v1/orders/{id}', { params: { path: { id } } })
      return { order: unwrap(result), etag: result.response.headers.get('ETag') }
    },
    retry: (count, error) => !(error instanceof ApiError && error.status < 500) && count < 2,
  })
}

export function nearbyQuery(id: string, radiusM: number) {
  return queryOptions({
    queryKey: queryKeys.nearby(id, radiusM),
    queryFn: async () =>
      unwrap(
        await api.GET('/api/v1/orders/{id}/nearby', {
          params: { path: { id }, query: { radiusM } },
        }),
      ),
    retry: false,
  })
}
